import { useSyncExternalStore } from 'react'
import { health } from './client'
import { ApiError } from './errors'
import type { HealthAnalyzer } from './types'

/**
 * Доступность сервиса автоматического разбора (docs/ANALYSIS.md).
 * `known: false` — бэкенд про разбор вообще не сообщает: либо он старый, либо
 * эндпоинты ещё не появились. Это не то же самое, что «сервис выключен», и
 * интерфейс объясняет случаи по-разному.
 */
export type AnalyzerStatus = {
  known: boolean
  available: boolean
  device: string
  gpu: string
  diarizer: string
  models: Record<string, string>
  detail: string
}

export const ANALYZER_UNKNOWN: AnalyzerStatus = {
  known: false,
  available: false,
  device: '',
  gpu: '',
  diarizer: '',
  models: {},
  detail: '',
}

function toStringMap(value: unknown): Record<string, string> {
  if (typeof value !== 'object' || value === null) return {}
  const result: Record<string, string> = {}
  for (const [key, item] of Object.entries(value)) {
    if (typeof item === 'string') result[key] = item
  }
  return result
}

/** Контракт не фиксирует форму поля — принимаем и флаг, и объект. */
export function normalizeAnalyzer(raw: HealthAnalyzer | null | undefined): AnalyzerStatus {
  if (raw === undefined || raw === null) return ANALYZER_UNKNOWN
  if (typeof raw === 'boolean') return { ...ANALYZER_UNKNOWN, known: true, available: raw }
  if (typeof raw !== 'object') return ANALYZER_UNKNOWN
  const available = raw.available ?? raw.ok ?? false
  return {
    known: true,
    available: Boolean(available),
    device: typeof raw.device === 'string' ? raw.device : '',
    gpu: typeof raw.gpu === 'string' ? raw.gpu : '',
    diarizer: typeof raw.diarizer === 'string' ? raw.diarizer : '',
    models: toStringMap(raw.models),
    detail: typeof raw.error === 'string' ? raw.error : '',
  }
}

export type ServerStatus = {
  /** null — ещё не проверяли. */
  online: boolean | null
  ffmpeg: boolean
  version: string
  analyzer: AnalyzerStatus
  lastError: string | null
  checkedAt: number
}

let state: ServerStatus = {
  online: null,
  ffmpeg: false,
  version: '',
  analyzer: ANALYZER_UNKNOWN,
  lastError: null,
  checkedAt: 0,
}

const listeners = new Set<() => void>()
let timer: number | null = null
let inFlight = false

function emit(next: ServerStatus): void {
  state = next
  for (const listener of listeners) listener()
}

export async function checkHealth(): Promise<ServerStatus> {
  if (inFlight) return state
  inFlight = true
  try {
    const result = await health()
    emit({
      online: result.ok !== false,
      ffmpeg: Boolean(result.ffmpeg),
      version: result.version ?? '',
      analyzer: normalizeAnalyzer(result.analyzer),
      lastError: null,
      checkedAt: Date.now(),
    })
  } catch (err) {
    const message = err instanceof ApiError ? err.userMessage : 'Сервер недоступен'
    emit({
      online: false,
      ffmpeg: false,
      version: '',
      analyzer: ANALYZER_UNKNOWN,
      lastError: message,
      checkedAt: Date.now(),
    })
  } finally {
    inFlight = false
  }
  return state
}

function schedule(): void {
  if (timer !== null) return
  const tick = (): void => {
    void checkHealth().then(() => {
      if (timer !== null) window.clearTimeout(timer)
      if (listeners.size === 0) {
        timer = null
        return
      }
      timer = window.setTimeout(tick, state.online ? 30_000 : 5_000)
    })
  }
  timer = window.setTimeout(tick, 0)
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  schedule()
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
  }
}

function getSnapshot(): ServerStatus {
  return state
}

export function useServerStatus(): ServerStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Пометить сервер недоступным по факту сетевой ошибки любого запроса. */
export function reportRequestFailure(err: unknown): void {
  if (err instanceof ApiError && err.offline) {
    emit({ ...state, online: false, lastError: err.userMessage, checkedAt: Date.now() })
    void checkHealth()
  }
}
